import { useEffect } from "react";
import { useSelector } from "react-redux";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { appInsightsService } from "./services/appInsights.service";
import { globalErrorHandler } from "./services/globalErrorHandler.service";
import "./css/chromeAutofill.css";
import { useSessionConfig } from "./hooks/useSessionConfig";
import { useSessionInit } from "./hooks/useSessionInit";
import SkypointLogoLoader from "./components/SkypointLogoLander/SkypointLogoLoader";
import { useMsalAuth } from "./hooks/useMsalAuth";
import useWindowDimensions from "./common/useWindowDimensions";
import { HeaderBanner } from "./components/HeaderBanner";
import SessionTimeout from "./components/shared/SessionTimeout";
import ErrorPage from "./components/shared/errorpage";
import InactivePlan from "./components/inactiveplan";
import { AppLayout } from "./components/AppLayout";
import { PackProvider } from "./components/packs/PackContext";
import { BlockedAccountScreen } from "./components/access/BlockedAccountScreen";
import { NoAccessScreen } from "./components/access/NoAccessScreen";
import { AccountErrorScreen } from "./components/access/AccountErrorScreen";
import { IRootReducerState } from "./store/IRootReducer";

const reactPlugin = appInsightsService.initialize();

// Initialize global error handlers
globalErrorHandler.initialize();

// Performance timing for login-to-landing measurement
const LOGIN_PERF_KEY = "__SKY_LOGIN_PERF__";

function AppNew() {
  const { currentUser, getQueryString } = useMsalAuth();

  const authState = useSelector((state: IRootReducerState) => state.authReducer);
  const { isError, errorMessage, errorCode } = authState.error;
  const isInactivePlan = authState.isInactivePlan;
  const isFullAccess = authState.isFullAccess;

  // Start performance timing on mount
  useEffect(() => {
    if (!(window as any)[LOGIN_PERF_KEY]) {
      (window as any)[LOGIN_PERF_KEY] = {
        startTime: performance.now(),
        appMounted: Date.now(),
      };
    }
  }, []);

  const { sessionDefault, setSessionDefault, isSessionDefaultLoaded, fetchSessionConfig } =
    useSessionConfig();

  const { sessionState, retry, isLoaded } = useSessionInit();

  const { windowWidth } = useWindowDimensions();

  useEffect(() => {
    document.body.classList.remove("hold-transition");
  }, []);

  // Log performance when app finishes loading
  useEffect(() => {
    if (isLoaded && (window as any)[LOGIN_PERF_KEY]) {
      const perf = (window as any)[LOGIN_PERF_KEY];
      const loadTime = performance.now() - perf.startTime;
      console.log(`[Performance] App loaded in ${loadTime.toFixed(0)}ms`);
      // Clear to avoid duplicate logs
      delete (window as any)[LOGIN_PERF_KEY];
    }
  }, [isLoaded]);

  const renderAppContent = () => {
    if (sessionState === "blocked") return <BlockedAccountScreen />;
    if (sessionState === "no-access") return <NoAccessScreen />;
    if (sessionState === "error") return <AccountErrorScreen onRetry={retry} />;
    if (sessionState === "loading" || !isLoaded) {
      // The BRANDED splash, not a bare word. SkypointLogoLoader (product logo ->
      // spinner -> "Powered by Skypoint") is what every other boot and route
      // gate in this app already renders — index.tsx uses it in six places,
      // ProtectedRoute and the auth callback in one each — and what the other
      // Skypoint products show on reload. This branch was simply missed, so the
      // very first thing a reload put on screen was unstyled text on white.
      return <SkypointLogoLoader />;
    }

    if (isError) {
      return (
        <ErrorPage
          show={true}
          message={errorMessage}
          errorCode={errorCode}
          title="Warning!"
        />
      );
    }

    if (isInactivePlan) {
      return <InactivePlan />;
    }

    // PackProvider mounts here — and only here — because this branch is
    // reached only when sessionState === "ready" && isLoaded, i.e. the
    // session gate (useSessionInit) has already resolved an instance
    // (Anchor 1). It is below <Provider store> (src/index.tsx) so RTK
    // Query + useTenantInstance work, and it wraps Header + Sidebar +
    // all routes so /crm/* pages and a future header mode-toggle can
    // usePacks().
    return (
      <PackProvider>
        {/* HeaderBanner lives INSIDE PackProvider so its calendar reconnect banner can
            usePacks() to gate on operator_crm + senior_living (same as the profile entry) —
            it must never fire a status read for users who cannot hold a calendar grant. */}
        <HeaderBanner />
        <AppLayout
          isFullAccess={isFullAccess}
          windowWidth={windowWidth}
          notificationMessage=""
          hideNewdashboard={false}
          setSessionDefault={setSessionDefault}
        />
      </PackProvider>
    );
  };

  return (
    <>
      {isSessionDefaultLoaded && (
        <SessionTimeout
          isInactivityActive={sessionDefault.isInactivityActive}
          inactivityLength={sessionDefault.inactivityLength}
          inactivityTimeout={sessionDefault.inactivityTimeout}
          isSessionExpiryActive={sessionDefault.isSessionExpiryActive}
          sessionLength={sessionDefault.sessionLength}
          sessionTimeout={sessionDefault.sessionTimeout}
        />
      )}
      {renderAppContent()}
    </>
  );
}

export default withAITracking(reactPlugin, AppNew);
